import React, { useMemo } from "react";
import classNames from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { Counterparty } from "../../../../common/types/counterparty";
import styles from "../RulesTable.module.scss";
import {
    TransactionRuleGroup,
    TransactionRule,
} from "../../../../common/types/transactionRule";
import { useTypedFlags } from "../../../../hooks/useTypedFlags";
import { pluralize } from "../../../../common/helpers/string";
import { GroupHeaderCell } from "./GroupHeaderCell";
import { RuleRow } from "./RuleRow";
import { isRuleUnreachable, isRuleDuplicate } from "./utils";

/**
 * RuleSortableRow wraps a rule row with sortable logic and group header cell.
 */
interface RuleSortableRowProps {
    readonly rule: TransactionRule;
    readonly ruleIndex: number;
    readonly group: TransactionRuleGroup;
    readonly overId: string | null;
    readonly groupListeners: React.HTMLAttributes<HTMLElement>;
    readonly groupAttributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly transactionCounts: Record<string, number>;
    readonly isCollapsed: boolean;
    readonly toggleCollapse: () => void;
    readonly groupIndex: number;
}

export const RuleSortableRow: React.FC<RuleSortableRowProps> = ({
    rule,
    ruleIndex,
    group,
    overId,
    groupListeners,
    groupAttributes,
    isGroupDragged,
    counterparties,
    transactionCounts,
    isCollapsed,
    toggleCollapse,
    groupIndex,
}) => {
    const { advancedRules } = useTypedFlags();
    const isOverGroup =
        overId === group.id || group.rules?.some((r) => r.id === overId);
    const { attributes, listeners, setNodeRef } = useSortable({ id: rule.id });

    const isUnreachable = useMemo(
        () => isRuleUnreachable(rule, group),
        [rule, group],
    );

    const isDuplicate = useMemo(
        () => isRuleDuplicate(rule, group),
        [rule, group],
    );

    if (isCollapsed && ruleIndex > 0) {
        return null;
    }

    return (
        <tr
            ref={setNodeRef}
            className={classNames(styles.rowHover, {
                [styles.unreachable]:
                    (advancedRules && isUnreachable) || rule.isDisabled,
            })}
            style={{ position: "relative" }}
        >
            {ruleIndex === 0 && (
                <GroupHeaderCell
                    groupIndex={groupIndex}
                    group={group}
                    groupListeners={groupListeners}
                    groupAttributes={groupAttributes}
                    toggleCollapse={toggleCollapse}
                />
            )}

            {!isCollapsed ? (
                <RuleRow
                    rule={rule}
                    isOver={overId === rule.id && !isGroupDragged}
                    listeners={listeners}
                    attributes={attributes}
                    isGroupDragged={isGroupDragged}
                    counterparties={counterparties}
                    transactionCounts={transactionCounts}
                    isUnreachable={isUnreachable}
                    isDuplicate={isDuplicate}
                />
            ) : (
                <td
                    colSpan={3}
                    className={classNames(styles.td, styles.rulesCount)}
                >
                    {group.rules?.length}{" "}
                    {pluralize("rule", group.rules?.length ?? 0)}
                </td>
            )}
            {isOverGroup && isGroupDragged && ruleIndex === 0 && (
                <>
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                            styles.withDot,
                        )}
                    />
                    <div
                        className={classNames(
                            styles.insertionIndicatorDot,
                            styles.top,
                        )}
                    />
                </>
            )}
        </tr>
    );
};
